import React, { useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { motion, AnimatePresence } from "framer-motion";
import { useExperience } from "../context/ExperienceContext";

const PALETTE_HEX = {
  regal: { left: "#d6c2ff", right: "#a970f0", emissive: "#7e22ce", bridge: "#f5d0fe" },
  calm: { left: "#a7d8d4", right: "#548a88", emissive: "#3c615f", bridge: "#cfe3e0" },
  grounded: { left: "#e2cfa8", right: "#a88854", emissive: "#6b5635", bridge: "#d9c29c" },
  tender: { left: "#e6c2bf", right: "#ad6e6a", emissive: "#734845", bridge: "#e0aba8" },
};

const ENERGY = { calm: 0.5, balanced: 1, electric: 2 };

const REGIONS = [
  { key: "frontal", label: "Frontal Lobe", where: "Front", position: [0.1, 0.55, 1.25], fn: "Focus, planning, and self-control.", edu: "Steady rhythm strengthens attention and impulse control — the foundation for staying on task and following multi-step instructions." },
  { key: "motor", label: "Motor Cortex", where: "Top-center strip", position: [0.0, 1.05, 0.15], fn: "Plans and directs voluntary movement.", edu: "Drumming patterns build fine and gross motor skills and hand coordination that transfer to writing and everyday tasks." },
  { key: "parietal", label: "Parietal Lobe", where: "Upper back", position: [0.0, 0.85, -0.65], fn: "Integrates touch, space, and timing.", edu: "Keeping a beat fuses sensation with movement — sharpening spatial awareness, sequencing, and early number sense." },
  { key: "temporal", label: "Temporal Lobe", where: "Sides (by the ears)", position: [1.0, -0.15, 0.35], fn: "Hearing, language, and memory.", edu: "Call-and-response drumming encodes information through sound, boosting language processing and recall." },
  { key: "occipital", label: "Occipital Lobe", where: "Back", position: [0.0, 0.25, -1.45], fn: "Processes what we see.", edu: "Watching and mirroring rhythmic patterns links vision to action — supporting reading readiness and pattern recognition." },
  { key: "cerebellum", label: "Cerebellum", where: "Lower back", position: [0.0, -0.85, -1.0], fn: "Coordination, balance, and timing.", edu: "The brain's rhythm-keeper — drumming trains the precise timing and sequencing that underpin learning and regulation." },
  { key: "corpus", label: "Corpus Callosum", where: "Deep center", position: [0.0, 0.15, 0.0], fn: "Bridges the two hemispheres.", edu: "Two-handed drumming strengthens the link between the logical and creative sides, helping the whole brain work together." },
];

function readPulse(pulseRef) {
  const { at, value } = pulseRef.current;
  const dt = (performance.now() - at) / 1000;
  return Math.max(0, value * Math.exp(-dt * 3));
}

function buildHemisphere(side) {
  const nT = 74, nP = 62, R = 1.5, sx = 0.82, sy = 1.02, sz = 1.5, gap = 0.04;
  const phi0 = side > 0 ? -Math.PI / 2 : Math.PI / 2;
  const phi1 = side > 0 ? Math.PI / 2 : (3 * Math.PI) / 2;
  const pos = new Float32Array(nT * nP * 3);
  let k = 0;
  for (let i = 0; i < nT; i++) {
    const theta = (i / (nT - 1)) * Math.PI;
    for (let j = 0; j < nP; j++) {
      const phi = phi0 + (j / (nP - 1)) * (phi1 - phi0);
      const fold = 0.07 * Math.sin(7 * theta) * Math.sin(8 * phi) + 0.05 * Math.sin(12 * theta + 1.3) * Math.cos(10 * phi) + 0.035 * Math.cos(13 * phi + 4 * theta);
      const r = R * (1 + fold);
      let x = r * Math.sin(theta) * Math.cos(phi) * sx;
      let y = r * Math.cos(theta) * sy;
      let z = r * Math.sin(theta) * Math.sin(phi) * sz;
      if (y < 0) y *= 0.5;
      if (z < 0) x *= 1 + 0.12 * (z / (R * sz));
      x += side * gap;
      pos[k++] = x; pos[k++] = y; pos[k++] = z;
    }
  }
  const indices = [];
  for (let i = 0; i < nT - 1; i++) for (let j = 0; j < nP - 1; j++) {
    const a = i * nP + j, b = a + 1, c = a + nP, d = c + 1;
    if (side > 0) indices.push(a, c, b, b, c, d); else indices.push(a, b, c, b, d, c);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  g.setIndex(indices);
  g.computeVertexNormals();
  return g;
}

function Hemisphere({ side, color, emissive, pulseRef, dim }) {
  const ref = useRef();
  const geo = useMemo(() => buildHemisphere(side), [side]);
  useFrame(() => {
    ref.current.material.emissiveIntensity = 0.2 + readPulse(pulseRef) * 0.7;
    ref.current.material.opacity += ((dim ? 0.45 : 0.92) - ref.current.material.opacity) * 0.1;
  });
  return (
    <mesh ref={ref} geometry={geo}>
      <meshStandardMaterial color={color} emissive={emissive} emissiveIntensity={0.2} roughness={0.55} metalness={0.15} transparent opacity={0.92} />
    </mesh>
  );
}

function Hotspot({ region, color, selected, anySelected, onSelect }) {
  const ref = useRef();
  useFrame((state) => {
    const beat = 1 + Math.sin(state.clock.elapsedTime * 3 + region.position[0]) * 0.12;
    let target;
    if (selected) target = 0.11 * beat;
    else if (anySelected) target = 0.04;
    else target = 0.06 * beat;
    ref.current.scale.setScalar(ref.current.scale.x + (target - ref.current.scale.x) * 0.2);
    ref.current.material.opacity = selected ? 1 : anySelected ? 0.3 : 0.8;
  });
  return (
    <mesh
      ref={ref}
      position={region.position}
      onClick={(e) => { e.stopPropagation(); onSelect(region.key); }}
      onPointerOver={(e) => { e.stopPropagation(); document.body.style.cursor = "pointer"; }}
      onPointerOut={() => { document.body.style.cursor = ""; }}
    >
      <sphereGeometry args={[1, 16, 16]} />
      <meshBasicMaterial color={color} transparent opacity={0.85} toneMapped={false} />
    </mesh>
  );
}

function LabelTracker({ groupRef, pos, labelRef }) {
  const { camera, size } = useThree();
  const v = useMemo(() => new THREE.Vector3(), []);
  useFrame(() => {
    const el = labelRef.current;
    if (!el) return;
    if (!pos || !groupRef.current) { el.style.opacity = "0"; return; }
    v.set(pos[0], pos[1], pos[2]).applyQuaternion(groupRef.current.quaternion).add(groupRef.current.position);
    const p = v.clone().project(camera);
    const x = (p.x * 0.5 + 0.5) * size.width;
    const y = (-p.y * 0.5 + 0.5) * size.height;
    el.style.transform = `translate(-50%, -160%) translate(${x}px, ${y}px)`;
    el.style.opacity = p.z < 1 ? "1" : "0";
  });
  return null;
}

function Brain({ palette, pulseRef, speed, ctrl, selected, onSelect, groupRef }) {
  const c = PALETTE_HEX[palette] || PALETTE_HEX.regal;
  useFrame((state, delta) => {
    const ct = ctrl.current;
    const paused = ct.dragging || selected;
    if (!paused) ct.targetY += delta * 0.18 * speed;
    groupRef.current.rotation.y += (ct.targetY - groupRef.current.rotation.y) * 0.12;
    const clampedX = Math.max(-0.9, Math.min(0.9, ct.targetX));
    groupRef.current.rotation.x += (clampedX - groupRef.current.rotation.x) * 0.12;
    groupRef.current.scale.setScalar(1 + readPulse(pulseRef) * 0.05);
  });
  return (
    <group ref={groupRef} position={[0, 0.1, 0]}>
      <Hemisphere side={-1} color={c.left} emissive={c.emissive} pulseRef={pulseRef} dim={!!selected} />
      <Hemisphere side={1} color={c.right} emissive={c.emissive} pulseRef={pulseRef} dim={!!selected} />
      {REGIONS.map((r) => (
        <Hotspot key={r.key} region={r} color={c.bridge} selected={selected === r.key} anySelected={!!selected} onSelect={onSelect} />
      ))}
    </group>
  );
}

function Lights() {
  return (
    <>
      <ambientLight intensity={0.75} />
      <directionalLight position={[3, 5, 4]} intensity={1.1} />
      <pointLight position={[-4, -2, 3]} intensity={0.5} />
    </>
  );
}

export default function BrainCanvas() {
  const { palette, energy, pulseRef, pulse } = useExperience();
  const speed = ENERGY[energy] || 1;
  const ctrl = useRef({ dragging: false, lastX: 0, lastY: 0, targetX: -0.14, targetY: 0 });
  const groupRef = useRef();
  const labelRef = useRef();
  const [selected, setSelected] = useState(null);
  const active = REGIONS.find((r) => r.key === selected);

  const select = (key) => { setSelected(key); pulse(0.6); };

  const onDown = (e) => { ctrl.current.dragging = true; ctrl.current.lastX = e.clientX; ctrl.current.lastY = e.clientY; };
  const onMove = (e) => {
    const ct = ctrl.current;
    if (!ct.dragging) return;
    ct.targetY += (e.clientX - ct.lastX) * 0.008;
    ct.targetX += (e.clientY - ct.lastY) * 0.008;
    ct.lastX = e.clientX; ct.lastY = e.clientY;
  };
  const onUp = () => { ctrl.current.dragging = false; };

  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-8 items-center" data-testid="brain-map">
      <div
        className="md:col-span-3 relative h-[380px] md:h-[520px] touch-none select-none cursor-grab active:cursor-grabbing"
        data-testid="brain-canvas"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerLeave={onUp}
      >
        {/* floating label pinned to the selected region on the brain */}
        <div
          ref={labelRef}
          data-testid="brain-region-label"
          className="absolute top-0 left-0 z-20 pointer-events-none glass shape-r px-3 py-1.5 font-mono text-[10px] tracking-[0.15em] uppercase text-fg whitespace-nowrap"
          style={{ opacity: 0, transition: "opacity 0.3s" }}
        >
          {active ? `${active.label} · ${active.where}` : ""}
        </div>
        <span className="absolute left-1/2 -translate-x-1/2 bottom-2 z-10 font-mono text-[10px] tracking-[0.25em] uppercase text-fg/40 pointer-events-none">
          Drag to rotate · tap a glowing node
        </span>
        <Canvas camera={{ position: [0, 0.3, 5.6], fov: 50 }} dpr={[1, 1.75]}>
          <Lights />
          <Brain palette={palette} pulseRef={pulseRef} speed={speed} ctrl={ctrl} selected={selected} onSelect={select} groupRef={groupRef} />
          <LabelTracker groupRef={groupRef} pos={active?.position} labelRef={labelRef} />
        </Canvas>
      </div>

      <div className="md:col-span-2">
        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-primary-c mb-3">The Learning Brain · Explore</p>
        <div className="flex flex-wrap gap-2 mb-5">
          {REGIONS.map((r) => (
            <button
              key={r.key}
              onClick={() => select(r.key)}
              data-testid={`region-btn-${r.key}`}
              title={`Location: ${r.where}`}
              className={`shape-r px-3 py-2 font-mono text-[10px] tracking-[0.1em] uppercase border transition-colors ${
                selected === r.key ? "bg-primary-c text-bg border-transparent" : "border-white/15 text-fg/60 hover:border-white/40"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {active ? (
            <motion.div key={active.key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }} className="glass shape-r p-6 relative" data-testid="region-info">
              <button onClick={() => setSelected(null)} data-testid="region-close" className="absolute top-4 right-4 text-fg/40 hover:text-fg font-mono text-sm" aria-label="Close">✕</button>
              <div className="flex items-center gap-2 mb-1 pr-6">
                <h3 className="font-serif text-2xl tracking-tight" data-testid="region-title">{active.label}</h3>
              </div>
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-fg/45 mb-3" data-testid="region-where">Location · {active.where}</p>
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-primary-c mb-4">{active.fn}</p>
              <p className="text-fg/75 leading-relaxed">{active.edu}</p>
            </motion.div>
          ) : (
            <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="glass shape-r p-6" data-testid="region-empty">
              <p className="text-fg/70 leading-relaxed md:text-lg">
                Each part of the brain learns a little differently. Tap a glowing node — or a label — to see where it sits and how rhythm and drumming light up that region to support real learning.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
